import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Shield, 
  Search, 
  RefreshCw, 
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
  Users,
  FileCheck,
  CheckCircle,
  Clock,
  Filter,
  Lock,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusOptions = [
  { value: "Report Submitted", label: "Report Submitted" },
  { value: "Documents Verified", label: "Documents Verified" },
  { value: "FIR Registered", label: "FIR Registered" },
  { value: "Investigation in Progress", label: "Investigation in Progress" },
  { value: "Partial Recovery", label: "Partial Recovery" },
  { value: "Full Recovery", label: "Full Recovery" },
  { value: "Closed - No Recovery", label: "Closed - No Recovery" },
];

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({
    total_amount_lost: 0,
    total_cases: 0,
    firs_filed: 0,
    cases_resolved: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCase, setSelectedCase] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const authStatus = sessionStorage.getItem("peff_admin_auth");
    if (authStatus === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCases();
      fetchStats();
    }
  }, [currentPage, statusFilter, isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter the admin password");
      return;
    }
    
    setIsLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("peff_admin_auth", "true");
      setIsAuthenticated(true);
      toast.success("Login successful!");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("peff_admin_auth");
    setIsAuthenticated(false);
    setPassword("");
    toast.success("Logged out successfully");
  };

  const fetchCases = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/admin/cases?page=${currentPage}&limit=10`;
      if (statusFilter && statusFilter !== "all") {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await axios.get(url);
      setCases(response.data.cases);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Failed to fetch cases:", error);
      toast.error("Failed to load cases");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    setIsUpdating(true);
    try {
      await axios.put(`${API}/cases/${selectedCase.case_id}/status`, {
        status: newStatus,
        notes: statusNotes
      });
      toast.success("Case status updated successfully");
      setSelectedCase(null);
      setNewStatus("");
      setStatusNotes("");
      fetchCases();
      fetchStats();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update case status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm("Are you sure you want to delete this case? This action cannot be undone.")) {
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/cases/${caseId}`);
      toast.success("Case deleted successfully");
      fetchCases();
      fetchStats();
    } catch (error) {
      console.error("Failed to delete case:", error);
      toast.error("Failed to delete case");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Full Recovery":
        return "bg-green-100 text-green-800";
      case "Partial Recovery":
        return "bg-amber-100 text-amber-800";
      case "Closed - No Recovery":
        return "bg-red-100 text-red-800";
      case "Investigation in Progress":
        return "bg-blue-100 text-blue-800";
      case "Documents Verified":
      case "FIR Registered":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const filteredCases = cases.filter(c => 
    c.case_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.victim.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.victim.phone.includes(searchTerm)
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center py-8" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <Card className="shadow-lg border-t-4 border-t-[#2c3e50]">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-[#2c3e50] rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                Admin Login
              </CardTitle>
              <p className="text-[#546e7a]">Enter password to access the dashboard</p>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                    Admin Password
                  </Label>
                  <Input
                    type="password"
                    placeholder="Enter admin password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-senior"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 rounded-lg"
                  data-testid="admin-login-btn"
                >
                  {isLoggingIn ? "Logging in..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#2c3e50] font-['Manrope'] flex items-center gap-3">
              <Shield className="w-8 h-8" />
              Admin Dashboard
            </h1>
            <p className="text-[#546e7a] mt-1">Manage and update case statuses</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => { fetchCases(); fetchStats(); }}
              variant="outline"
              className="border-2 border-[#2c3e50]"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-2 border-[#e74c3c] text-[#e74c3c] hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-[#e74c3c]" data-testid="admin-stat-amount">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <IndianRupee className="w-8 h-8 text-[#e74c3c]" />
                <div>
                  <p className="text-sm text-[#546e7a]">Total Lost</p>
                  <p className="text-2xl font-bold text-[#2c3e50]">{formatCurrency(stats.total_amount_lost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-[#2c3e50]" data-testid="admin-stat-cases">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-[#2c3e50]" />
                <div>
                  <p className="text-sm text-[#546e7a]">Total Cases</p>
                  <p className="text-2xl font-bold text-[#2c3e50]">{stats.total_cases}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-[#3498db]" data-testid="admin-stat-firs">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileCheck className="w-8 h-8 text-[#3498db]" />
                <div>
                  <p className="text-sm text-[#546e7a]">FIRs Filed</p>
                  <p className="text-2xl font-bold text-[#2c3e50]">{stats.firs_filed}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-[#27ae60]" data-testid="admin-stat-resolved">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-[#27ae60]" />
                <div>
                  <p className="text-sm text-[#546e7a]">Resolved</p>
                  <p className="text-2xl font-bold text-[#2c3e50]">{stats.cases_resolved}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#95a5a6]" />
                  <Input
                    type="text"
                    placeholder="Search by Case ID, Name, or Phone"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div className="w-full md:w-64">
                <Label className="text-sm font-medium mb-1 block">Filter by Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statusOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cases Table */}
        <Card data-testid="cases-table-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-[#2c3e50]">
              Cases ({filteredCases.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8 text-center text-[#546e7a]">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                Loading cases...
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="p-8 text-center text-[#546e7a]">
                No cases found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Case ID</TableHead>
                      <TableHead>Victim</TableHead>
                      <TableHead>Amount Lost</TableHead>
                      <TableHead>Scam Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCases.map((caseItem) => (
                      <TableRow key={caseItem.case_id} data-testid={`case-row-${caseItem.case_id}`}>
                        <TableCell className="font-mono text-sm">{caseItem.case_id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{caseItem.victim.name}</p>
                            <p className="text-sm text-[#546e7a]">{caseItem.victim.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold text-[#e74c3c]">
                          {formatCurrency(caseItem.financial.amount_lost)}
                        </TableCell>
                        <TableCell>{caseItem.scam.scam_type}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(caseItem.status)}`}>
                            {caseItem.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(caseItem.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedCase(caseItem);
                                    setNewStatus(caseItem.status);
                                  }}
                                  data-testid={`update-btn-${caseItem.case_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle className="text-xl font-bold text-[#2c3e50]">
                                    Update Case: {caseItem.case_id}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="text-[#546e7a]">Victim</p>
                                      <p className="font-semibold">{caseItem.victim.name}</p>
                                    </div>
                                    <div>
                                      <p className="text-[#546e7a]">Amount Lost</p>
                                      <p className="font-semibold text-[#e74c3c]">{formatCurrency(caseItem.financial.amount_lost)}</p>
                                    </div>
                                    <div>
                                      <p className="text-[#546e7a]">Scam Type</p>
                                      <p className="font-semibold">{caseItem.scam.scam_type}</p>
                                    </div>
                                    <div>
                                      <p className="text-[#546e7a]">Current Status</p>
                                      <p className="font-semibold">{caseItem.status}</p>
                                    </div>
                                  </div>

                                  <div>
                                    <Label className="text-base font-semibold">Update Status</Label>
                                    <Select value={newStatus} onValueChange={setNewStatus}>
                                      <SelectTrigger className="mt-2" data-testid="new-status-select">
                                        <SelectValue placeholder="Select new status" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {statusOptions.map(option => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <div>
                                    <Label className="text-base font-semibold">Notes (Optional)</Label>
                                    <Textarea
                                      placeholder="Add notes about this status update..."
                                      value={statusNotes}
                                      onChange={(e) => setStatusNotes(e.target.value)}
                                      className="mt-2"
                                      data-testid="status-notes"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button
                                    onClick={handleUpdateStatus}
                                    disabled={isUpdating}
                                    className="bg-[#2c3e50] hover:bg-[#1a252f]"
                                    data-testid="save-status-btn"
                                  >
                                    {isUpdating ? "Updating..." : "Save Changes"}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-[#e74c3c] border-[#e74c3c] hover:bg-red-50"
                              onClick={() => handleDeleteCase(caseItem.case_id)}
                              disabled={isDeleting}
                              data-testid={`delete-btn-${caseItem.case_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-[#546e7a]">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
